import React, { createContext, useState, useContext, useEffect } from 'react';

const LanguageContext = createContext();

export const translations = {
  en: {
    nav: {
      dashboard: 'Dashboard',
      strategy: 'Strategy',
      performance: 'Performance',
      telegram: 'Telegram',
      live: 'LIVE'
    },
    dashboard: {
      title: 'Trading Assets',
      totalSignals: 'Total Signals',
      winRate: 'Win Rate',
      winningSignals: 'Winning Signals',
      avgRR: 'Avg Risk/Reward',
      viewDetails: 'View Details',
      confidence: 'Confidence',
      loading: 'Loading market data...'
    },
    asset: {
      backToDashboard: 'Back to Dashboard',
      confidence: 'Confidence',
      riskReward: 'Risk/Reward',
      timeframe: 'Timeframe',
      priceChart: 'Price Chart',
      entryPrice: 'Entry Price',
      stopLoss: 'Stop Loss',
      takeProfit: 'Take Profit',
      rsi: 'RSI (14)',
      macd: 'MACD',
      movingAverages: 'Moving Averages',
      supportLevel: 'Support Level',
      resistanceLevel: 'Resistance Level',
      signalReasons: 'Signal Reasons'
    },
    strategy: {
      title: 'Trading Strategy',
      subtitle: 'Professional trading strategy combining four technical analysis tools for accurate and reliable signals',
      components: 'Strategy Components',
      supportResistance: 'Support & Resistance',
      supportResistanceDesc: 'Identify key price levels where price tends to bounce or break through',
      rsiIndicator: 'RSI Indicator',
      rsiIndicatorDesc: 'Measures momentum strength and speed of price movement to identify overbought/oversold zones',
      macdIndicator: 'MACD Indicator',
      macdIndicatorDesc: 'Detects trend changes and momentum through the relationship between two moving averages',
      movingAveragesIndicator: 'Moving Averages',
      movingAveragesIndicatorDesc: 'Determines overall market trend through average price over a time period',
      buySignalTitle: 'BUY Signal Conditions',
      buyCondition1: 'RSI below 40',
      buyCondition1Desc: 'Indicates the asset is in oversold territory and likely to bounce back up',
      buyCondition2: 'MACD Histogram positive',
      buyCondition2Desc: 'Confirms the start of strong upward momentum',
      buyCondition3: 'Price above MA50',
      buyCondition3Desc: 'Indicates short-term upward trend',
      buyCondition4: 'Price near support level',
      buyCondition4Desc: 'Entry opportunity at a likely bounce point',
      buyExample: 'Entry Example: If RSI = 35, MACD Histogram = 0.002, and price is above MA50 and near support, this is a strong buy signal with high confidence.',
      sellSignalTitle: 'SELL Signal Conditions',
      sellCondition1: 'RSI above 60',
      sellCondition1Desc: 'Indicates the asset is in overbought territory and likely to pull back',
      sellCondition2: 'MACD Histogram negative',
      sellCondition2Desc: 'Confirms the start of downward momentum',
      sellCondition3: 'Price below MA50',
      sellCondition3Desc: 'Indicates short-term downward trend',
      sellCondition4: 'Price near resistance level',
      sellCondition4Desc: 'Sell opportunity at a likely bounce point downward',
      sellExample: 'Entry Example: If RSI = 68, MACD Histogram = -0.003, and price is below MA50 and near resistance, this is a strong sell signal.',
      riskManagement: 'Risk Management',
      stopLossTitle: 'Stop Loss',
      stopLossDesc: 'Stop Loss is determined based on support and resistance levels:',
      stopLossBuy: 'For BUY: Place Stop Loss below support level by 0.5%',
      stopLossSell: 'For SELL: Place Stop Loss above resistance level by 0.5%',
      takeProfitTitle: 'Take Profit',
      takeProfitDesc: 'Two targets are set for taking profits:',
      tp1: 'TP1: At 1% profit from entry price',
      tp2: 'TP2: At 2% profit from entry price',
      importantNote: 'Important Note',
      importantNoteDesc: 'Risk/Reward ratio should be at least 1:2. This means potential profit should be double the potential loss.',
      timeframes: 'Timeframes',
      timeframe5m: '5 Minutes',
      timeframe5mDesc: 'Suitable for fast trading and scalping',
      timeframe15m: '15 Minutes',
      timeframe15mDesc: 'Recommended timeframe for daily trading',
      timeframe1h: '1 Hour',
      timeframe1hDesc: 'Suitable for patient trading and medium-term positions'
    },
    performance: {
      title: 'Performance Analytics',
      subtitle: 'Comprehensive analysis of signal performance and success rates across all assets',
      totalSignals: 'Total Signals',
      winRate: 'Win Rate',
      winningSignals: 'Winning Signals',
      avgRR: 'Avg Risk/Reward',
      signalDistribution: 'Signal Distribution',
      confidenceLevels: 'Confidence Levels by Asset',
      currentSignals: 'Current Signals',
      asset: 'Asset',
      signal: 'Signal',
      confidence: 'Confidence',
      entry: 'Entry',
      stopLoss: 'Stop Loss',
      takeProfit: 'Take Profit',
      rr: 'R/R'
    },
    telegram: {
      title: 'Telegram Notifications',
      subtitle: 'Receive real-time trading signals directly to your Telegram',
      howToSetup: 'How to Setup',
      step1Title: 'Create Your Bot',
      step1Desc: 'Open Telegram and search for @BotFather. Send /newbot command and follow instructions to create your bot.',
      step2Title: 'Get Your Chat ID',
      step2Desc: 'Send any message to your bot, then click the button below to get your Chat ID.',
      step3Title: 'Subscribe',
      step3Desc: 'Enter your Chat ID below and click Subscribe to start receiving signals.',
      getChatId: 'Get My Chat ID',
      chatIdPlaceholder: 'Enter your Telegram Chat ID',
      subscribe: 'Subscribe to Notifications',
      unsubscribe: 'Unsubscribe',
      status: 'Status',
      active: 'Active',
      inactive: 'Inactive',
      subscribed: 'You are subscribed to Telegram notifications!',
      notSubscribed: 'Not subscribed yet',
      whatYouGet: 'What You Get',
      feature1: 'Instant notifications for high-confidence signals (>70%)',
      feature2: 'Complete signal details: Entry, Stop Loss, Take Profit',
      feature3: 'Risk/Reward ratio for each signal',
      feature4: 'Real-time updates every time market conditions change',
      botTokenRequired: 'Bot Token Required',
      botTokenDesc: 'To enable Telegram notifications, please add your Telegram Bot Token to the backend .env file.'
    },
    common: {
      loading: 'Loading...',
      buy: 'BUY',
      sell: 'SELL',
      neutral: 'NEUTRAL',
      crypto: 'Crypto',
      commodity: 'Commodity',
      forex: 'Forex',
      index: 'Index'
    }
  },
  ar: {
    nav: {
      dashboard: 'الإشارات',
      strategy: 'الاستراتيجية',
      performance: 'الأداء',
      telegram: 'تلجرام',
      live: 'مباشر'
    },
    dashboard: {
      title: 'الأصول التداولية',
      totalSignals: 'إجمالي الإشارات',
      winRate: 'نسبة النجاح',
      winningSignals: 'الإشارات الرابحة',
      avgRR: 'متوسط المخاطرة/العائد',
      viewDetails: 'عرض التفاصيل',
      confidence: 'الثقة',
      loading: 'جاري تحميل بيانات السوق...'
    },
    asset: {
      backToDashboard: 'العودة للإشارات',
      confidence: 'الثقة',
      riskReward: 'المخاطرة/العائد',
      timeframe: 'الإطار الزمني',
      priceChart: 'الرسم البياني للسعر',
      entryPrice: 'سعر الدخول',
      stopLoss: 'وقف الخسارة',
      takeProfit: 'جني الأرباح',
      rsi: 'مؤشر القوة النسبية (14)',
      macd: 'مؤشر MACD',
      movingAverages: 'المتوسطات المتحركة',
      supportLevel: 'مستوى الدعم',
      resistanceLevel: 'مستوى المقاومة',
      signalReasons: 'أسباب الإشارة'
    },
    strategy: {
      title: 'استراتيجية التداول',
      subtitle: 'استراتيجية تداول احترافية تجمع بين أربع أدوات تحليل فني لتوفير إشارات دقيقة وموثوقة',
      components: 'مكونات الاستراتيجية',
      supportResistance: 'الدعوم والمقاومات',
      supportResistanceDesc: 'تحديد مستويات الأسعار الرئيسية التي يميل السعر للارتداد منها أو اختراقها',
      rsiIndicator: 'مؤشر RSI',
      rsiIndicatorDesc: 'يقيس قوة الزخم وسرعة حركة السعر لتحديد مناطق التشبع',
      macdIndicator: 'مؤشر MACD',
      macdIndicatorDesc: 'يكشف تغيرات الاتجاه والزخم من خلال العلاقة بين متوسطين متحركين',
      movingAveragesIndicator: 'المتوسطات المتحركة',
      movingAveragesIndicatorDesc: 'تحدد الاتجاه العام للسوق من خلال متوسط السعر خلال فترة زمنية',
      buySignalTitle: 'شروط إشارة الشراء (BUY)',
      buyCondition1: 'RSI أقل من 40',
      buyCondition1Desc: 'يشير إلى أن الأصل في منطقة تشبع بيعي ومن المحتمل أن يرتد للأعلى',
      buyCondition2: 'MACD Histogram إيجابي',
      buyCondition2Desc: 'يؤكد بداية زخم صعودي قوي',
      buyCondition3: 'السعر فوق MA50',
      buyCondition3Desc: 'يشير إلى اتجاه صعودي قصير المدى',
      buyCondition4: 'السعر قريب من مستوى الدعم',
      buyCondition4Desc: 'فرصة دخول عند نقطة ارتداد محتملة',
      buyExample: 'مثال على الدخول: إذا كان RSI = 35، MACD Histogram = 0.002، والسعر فوق MA50 وقريب من الدعم، فهذه إشارة شراء قوية بثقة عالية.',
      sellSignalTitle: 'شروط إشارة البيع (SELL)',
      sellCondition1: 'RSI أكثر من 60',
      sellCondition1Desc: 'يشير إلى أن الأصل في منطقة تشبع شرائي ومن المحتمل أن يتراجع',
      sellCondition2: 'MACD Histogram سلبي',
      sellCondition2Desc: 'يؤكد بداية زخم هبوطي',
      sellCondition3: 'السعر تحت MA50',
      sellCondition3Desc: 'يشير إلى اتجاه هبوطي قصير المدى',
      sellCondition4: 'السعر قريب من مستوى المقاومة',
      sellCondition4Desc: 'فرصة بيع عند نقطة ارتداد محتملة للأسفل',
      sellExample: 'مثال على الدخول: إذا كان RSI = 68، MACD Histogram = -0.003، والسعر تحت MA50 وقريب من المقاومة، فهذه إشارة بيع قوية.',
      riskManagement: 'إدارة المخاطر',
      stopLossTitle: 'وقف الخسارة',
      stopLossDesc: 'يتم تحديد نقطة Stop Loss بناءً على مستويات الدعم والمقاومة:',
      stopLossBuy: 'للشراء: يوضع Stop Loss أسفل مستوى الدعم بنسبة 0.5%',
      stopLossSell: 'للبيع: يوضع Stop Loss أعلى مستوى المقاومة بنسبة 0.5%',
      takeProfitTitle: 'جني الأرباح',
      takeProfitDesc: 'يتم تحديد هدفين لجني الأرباح:',
      tp1: 'TP1: عند تحقيق ربح 1% من سعر الدخول',
      tp2: 'TP2: عند تحقيق ربح 2% من سعر الدخول',
      importantNote: 'ملاحظة مهمة',
      importantNoteDesc: 'نسبة المخاطرة إلى العائد (Risk/Reward) يجب أن تكون 1:2 على الأقل. أي أن الربح المحتمل يجب أن يكون ضعف الخسارة المحتملة.',
      timeframes: 'الأطر الزمنية',
      timeframe5m: '5 دقائق',
      timeframe5mDesc: 'مناسب للتداول السريع والمضاربات اللحظية',
      timeframe15m: '15 دقيقة',
      timeframe15mDesc: 'الإطار الموصى به للتداول اليومي',
      timeframe1h: 'ساعة واحدة',
      timeframe1hDesc: 'مناسب للتداول المتأني والصفقات متوسطة المدى'
    },
    performance: {
      title: 'تحليل الأداء',
      subtitle: 'تحليل شامل لأداء الإشارات ونسب النجاح عبر جميع الأصول',
      totalSignals: 'إجمالي الإشارات',
      winRate: 'نسبة النجاح',
      winningSignals: 'الإشارات الرابحة',
      avgRR: 'متوسط المخاطرة/العائد',
      signalDistribution: 'توزيع الإشارات',
      confidenceLevels: 'مستويات الثقة حسب الأصل',
      currentSignals: 'الإشارات الحالية',
      asset: 'الأصل',
      signal: 'الإشارة',
      confidence: 'الثقة',
      entry: 'الدخول',
      stopLoss: 'وقف الخسارة',
      takeProfit: 'جني الأرباح',
      rr: 'م/ع'
    },
    telegram: {
      title: 'إشعارات تلجرام',
      subtitle: 'استقبل إشارات التداول مباشرة على تلجرام الخاص بك',
      howToSetup: 'كيفية الإعداد',
      step1Title: 'أنشئ البوت الخاص بك',
      step1Desc: 'افتح تلجرام وابحث عن @BotFather. أرسل الأمر /newbot واتبع التعليمات لإنشاء البوت الخاص بك.',
      step2Title: 'احصل على Chat ID',
      step2Desc: 'أرسل أي رسالة للبوت الخاص بك، ثم اضغط على الزر أدناه للحصول على Chat ID.',
      step3Title: 'اشترك',
      step3Desc: 'أدخل Chat ID الخاص بك أدناه واضغط اشترك لبدء استقبال الإشارات.',
      getChatId: 'احصل على Chat ID',
      chatIdPlaceholder: 'أدخل Chat ID من تلجرام',
      subscribe: 'الاشتراك في الإشعارات',
      unsubscribe: 'إلغاء الاشتراك',
      status: 'الحالة',
      active: 'نشط',
      inactive: 'غير نشط',
      subscribed: 'أنت مشترك في إشعارات تلجرام!',
      notSubscribed: 'غير مشترك بعد',
      whatYouGet: 'ما ستحصل عليه',
      feature1: 'إشعارات فورية للإشارات عالية الثقة (>70%)',
      feature2: 'تفاصيل كاملة للإشارة: الدخول، وقف الخسارة، جني الأرباح',
      feature3: 'نسبة المخاطرة/العائد لكل إشارة',
      feature4: 'تحديثات فورية كلما تغيرت ظروف السوق',
      botTokenRequired: 'يتطلب Bot Token',
      botTokenDesc: 'لتفعيل إشعارات تلجرام، يرجى إضافة Telegram Bot Token في ملف .env الخاص بالـ backend.'
    },
    common: {
      loading: 'جاري التحميل...',
      buy: 'شراء',
      sell: 'بيع',
      neutral: 'محايد',
      crypto: 'عملة رقمية',
      commodity: 'سلعة',
      forex: 'فوركس',
      index: 'مؤشر'
    }
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('ar');

  useEffect(() => {
    const savedLang = localStorage.getItem('language') || 'ar';
    setLanguage(savedLang);
    document.documentElement.dir = savedLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = savedLang;
  }, []);

  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'ar' : 'en';
    setLanguage(newLang);
    localStorage.setItem('language', newLang);
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLang;
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
